import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, ExternalLink } from '../../icons/wp-icons';
import { outlookCalendarApi } from '../../api/outlookCalendarApi';

// Brand-akzentuiertes Kalender-Icon in Outlook-Blau.
const OutlookCalendarIcon = ({ className = 'w-7 h-7' }) => (
  <svg viewBox="0 0 48 48" className={className} xmlns="http://www.w3.org/2000/svg" fill="none">
    <rect x="8" y="10" width="32" height="30" rx="3" fill="#fff" stroke="#0078D4" strokeWidth="2.5" />
    <rect x="8" y="10" width="32" height="8" fill="#0078D4" />
    <line x1="16" y1="6" x2="16" y2="14" stroke="#0078D4" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="32" y1="6" x2="32" y2="14" stroke="#0078D4" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="16" cy="26" r="2" fill="#0078D4" />
    <circle cx="24" cy="26" r="2" fill="#0078D4" />
    <circle cx="32" cy="26" r="2" fill="#0078D4" />
    <circle cx="16" cy="33" r="2" fill="#0078D4" />
    <circle cx="24" cy="33" r="2" fill="#0078D4" />
  </svg>
);

function OutlookCalendarSettings({ readOnly = false }) {
  const [connected, setConnected] = useState(false);
  const [accountEmail, setAccountEmail] = useState(null);
  const [tokenInvalid, setTokenInvalid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const data = await outlookCalendarApi.getStatus();
      setConnected(data.connected);
      setAccountEmail(data.email || null);
      setTokenInvalid(!!data.tokenInvalid);
    } catch (error) {
      console.error('Fehler beim Prüfen des Outlook Calendar Status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);

      // Popup SOFORT öffnen (mobile Browser blockieren sonst)
      const width = 550;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        'about:blank',
        'Outlook Calendar OAuth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no`
      );

      if (!popup) {
        alert('Bitte erlauben Sie Popups für diese Seite, um Outlook Calendar zu verbinden.');
        setConnecting(false);
        return;
      }

      popup.document.write('<html><body style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;background:#f9fafb;"><p>Laden...</p></body></html>');

      const { authUrl } = await outlookCalendarApi.getAuthUrl();
      popup.location.href = authUrl;

      const checkPopup = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopup);
          setConnecting(false);
          setTimeout(() => { checkStatus(); }, 500);
        }
      }, 500);

    } catch (error) {
      console.error('Fehler beim Verbinden mit Outlook Calendar:', error);
      alert('Fehler beim Verbinden mit Outlook Calendar');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Outlook Calendar Verbindung wirklich trennen?')) return;

    try {
      await outlookCalendarApi.disconnect();
      setConnected(false);
      setAccountEmail(null);
      alert('Outlook Calendar Verbindung getrennt');
    } catch (error) {
      console.error('Fehler beim Trennen:', error);
      alert('Fehler beim Trennen der Verbindung');
    }
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card p-6 border-t-4" style={{ borderTopColor: '#0078D4' }}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-white border border-gray-200 flex items-center justify-center shadow-sm">
            <OutlookCalendarIcon className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-semibold">Outlook Calendar</h2>
        </div>

        {connected && tokenInvalid ? (
          <div className="flex items-center gap-2 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
            <XCircle className="w-4 h-4" />
            Verbindung abgelaufen
          </div>
        ) : connected ? (
          <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
            <CheckCircle className="w-4 h-4" />
            Verbunden{accountEmail ? ` (${accountEmail})` : ''}
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
            <XCircle className="w-4 h-4" />
            Nicht verbunden
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-blue-900">
          <strong>Synchronisation:</strong> Wenn Sie Ihr Outlook Calendar verbinden,
          werden alle Termine automatisch zwischen CRM und Outlook synchronisiert.
        </p>
        <ul className="mt-2 text-sm text-blue-800 space-y-1 ml-4">
          <li>Neuer Termin im CRM - Automatisch in Outlook</li>
          <li>Termin im CRM ändern - Update in Outlook</li>
          <li>Termin im CRM löschen - Löschen in Outlook</li>
          <li>Alle Kalender Ihres Outlook-Kontos werden für Verfügbarkeitsprüfungen berücksichtigt</li>
          <li>Kunden erhalten automatisch Termin-Einladungen</li>
        </ul>
      </div>

      {connected && tokenInvalid && (
        <div className="p-4 mb-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-900 font-medium">Die Verbindung zu Outlook Calendar ist abgelaufen oder wurde widerrufen.</p>
          <p className="text-sm text-red-700 mt-1">
            Termine werden aktuell NICHT mehr synchronisiert. Bitte trennen Sie die Verbindung und verbinden Sie den Kalender neu.
            Bereits gespiegelte Termine werden dabei nicht doppelt angelegt.
          </p>
          {!readOnly && (
            <button
              onClick={async () => { await handleDisconnect(); }}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded-button hover:bg-red-700 transition text-sm font-medium"
            >
              Verbindung trennen und neu verbinden
            </button>
          )}
        </div>
      )}

      {connected ? (
        <div className="space-y-4">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-900 font-medium">
              Ihr Outlook Calendar ist verbunden
            </p>
            <p className="text-sm text-green-700 mt-1">
              Alle Termine werden automatisch synchronisiert.
            </p>
          </div>

          {!readOnly && (
            <button
              onClick={handleDisconnect}
              className="w-full px-4 py-3 bg-danger text-white rounded-button hover:bg-red-700 transition font-medium"
            >
              Verbindung trennen
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-text font-medium">
              Outlook Calendar verbinden
            </p>
            <p className="text-sm text-text-muted mt-1">
              {readOnly
                ? 'Sie haben keine Berechtigung, diese Einstellung zu ändern.'
                : 'Klicken Sie auf "Verbinden" um Ihr Outlook Calendar zu verknüpfen. Sie werden zu Microsoft weitergeleitet um die Verbindung zu autorisieren.'}
            </p>
          </div>

          {!readOnly && (
            <button
              onClick={handleConnect}
              disabled={connecting}
              className="w-full px-4 py-3 text-white rounded-button hover:opacity-90 transition font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: '#0078D4' }}
            >
              {connecting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Verbinde...
                </>
              ) : (
                <>
                  <ExternalLink className="w-5 h-5" />
                  Mit Outlook Calendar verbinden
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default OutlookCalendarSettings;
